import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiSideNav, EuiIcon, EuiSideNavItemType, EuiText, EuiFlexGroup, EuiFlexItem, EuiLink } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'
import { EuiSelectableOption } from '@elastic/eui/src/components/selectable/selectable_option'

import { Account, PermissionType } from 'api/interfaces'
import { useGetAIPlanBlueprintsQuery } from 'api/rtkQueryApi/platform/aiPlanBlueprintsApi'
import { useGetAIPlansQuery } from 'api/rtkQueryApi/platform/aiPlansApi'
import { useGetMasterCampaignPlansQuery } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'
import { initializeCampaignCreate } from 'features/builders/CampaignCreateSlice'
import { CreateCampaignStorageManager } from 'features/builders/createCampaign/CreateCampaignStorageManager'
import { useCampaignApproval } from 'hooks/useCampaignApproval'
import history from 'services/HistoryService'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

import { useGetAccountsQuery } from '../../api/rtkQueryApi/platform/accountsApi'
import { showSuccessToast, updateAccountState } from '../../app/appSlice'
import { useOrganizationPermissions } from '../../providers/OrganizationPermissionsContext'
import { useIsAgency } from '../../whiteLabel/useIsAgency'
import { AcSearchSelectorPopover } from '../Basic/AcSearchSelectorPopover'
import { SubNavItem } from './SubNavItem'

export const AdCritterAiSideNav: React.FC = () => {
  const dispatch = useDispatch()
  const isAgency = useIsAgency()
  const { currentUser, currentOrganization, isVendastaUser, isShopifyUser, currentAccount, isStaffUser, showInternalView } = useSelector((state: RootState) => state.app)
  const { campaign } = useSelector((state: RootState) => state.campaign)
  const whiteLabel = useWhiteLabel()
  const { hasPermission } = useOrganizationPermissions()
  const canUseTemplateBuilder = hasPermission(PermissionType.UseTemplateBuilderCampaignCreation)
  const { isEnabled: campaignApprovalEnabled, canCreate: canCreateCampaigns } = useCampaignApproval()
  const masterCampaignPlansQuery = useGetMasterCampaignPlansQuery({
    organizationId: currentOrganization?.id ?? ''
  })
  const aiPlanBlueprintsQuery = useGetAIPlanBlueprintsQuery({
    organizationId: currentOrganization?.id ?? '',
    showHidden: isStaffUser && showInternalView
  })
  const aiPlansQuery = useGetAIPlansQuery({
    organizationId: currentOrganization?.id ?? '',
    showHidden: isStaffUser && showInternalView
  })

  const accountsQuery = useGetAccountsQuery({ isAgency: isAgency })
  const [accountOptions, setAccountOptions] = useState<EuiSelectableOption[]>([])

  const [navItems, setNavItems] = useState<EuiSideNavItemType<any>[]>([])

  useEffect(() => {
    if (accountsQuery.data)
      setAccountOptions(
        accountsQuery.data.map(a => ({
          label: a.name,
          key: a.id,
          onClick: () => onSelectAccount(a),
          checked: undefined,
          'aria-label': a.name,
          prepend: <EuiIcon type='package' size='m' />
        }))
      )
  }, [accountsQuery.data])

  const onSelectAccount = (account: Account) => {
    dispatch(updateAccountState(account))
    dispatch(showSuccessToast(`Switch account to ${account.name}`))
  }

  const onNewClientClick = () => {
    history.push('/create-client')
  }

  useEffect(() => {
    const items: EuiSideNavItemType<any>[] = []

    const clientItem: EuiSideNavItemType<any> = {
      id: 'currentClient',
      name: `Current Client`,
      renderItem: () => (
        <div style={{ marginLeft: '-12px', backgroundColor: '#393a47', padding: '12px' }}>
          <React.Fragment>
            <EuiFlexGroup gutterSize={'none'}>
              <EuiFlexItem grow={false} style={{ paddingTop: '4px' }}>
                <EuiIcon type={'layers'} color={'ghost'} size={'m'} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiText size='m' color='ghost' style={{ padding: '0 8px 8px' }}>
                  <p>Current Client:</p>
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiText size='s' color='ghost' style={{ padding: '0 24px 8px' }}>
              <p>{currentAccount?.name}</p>
            </EuiText>
            <EuiText size='s' color='ghost' textAlign='right'>
              <AcSearchSelectorPopover isLoading={accountsQuery.isLoading} options={accountOptions} currentValue={currentAccount?.name} linkText={'Create / Change Client'} popoverAnchorPosition={'downCenter'} footerButtonText={'Create New Client'} onNewClick={onNewClientClick} />
            </EuiText>
          </React.Fragment>
        </div>
      )
    }

    items.push(clientItem)

    items.push({
      id: 'adcritter-ai',
      name: 'AdCritter AI',
      icon: <EuiIcon type={'machineLearningApp'} />,
      renderItem: props => (
        <EuiLink onClick={props.onClick}>
          <EuiText size={'s'}>
            <b>{props.children}</b>
          </EuiText>
        </EuiLink>
      ),
      items: [
        {
          id: 'ai-blueprints',
          name: 'AI Blueprints',
          renderItem: props => <SubNavItem {...props} isSelected={history.location.pathname.startsWith('/ai/blueprints')} />,
          onClick: () => {
            if (!aiPlanBlueprintsQuery.data || aiPlanBlueprintsQuery.data.length <= 0) {
              history.push('/ai/blueprints/welcome')
              return
            }

            history.push('/ai/blueprints')
          }
        },
        {
          id: 'ai-plans',
          name: 'AI Plans',
          renderItem: props => <SubNavItem {...props} isSelected={history.location.pathname.startsWith('/ai/plans')} />,
          onClick: () => {
            if (!aiPlansQuery.data || aiPlansQuery.data.length <= 0) {
              history.push('/ai/plans/welcome')
              return
            }

            history.push('/ai/plans')
          }
        },
        ...(!hasPermission(PermissionType.HideLegacyPlanner) && masterCampaignPlansQuery.data && masterCampaignPlansQuery.data.length > 0
          ? [
              {
                id: 'legacy-planner',
                name: 'Legacy Planner',
                renderItem: (props: any) => <SubNavItem {...props} isSelected={history.location.pathname === '/ai' || history.location.pathname === '/ai/welcome' || (history.location.pathname.startsWith('/ai/') && !history.location.pathname.startsWith('/ai/blueprints') && !history.location.pathname.startsWith('/ai/plans'))} />,
                onClick: () => {
                  history.push('/ai')
                }
              }
            ]
          : [])
      ],
      onClick: () => {
        if (!aiPlanBlueprintsQuery.data || aiPlanBlueprintsQuery.data.length <= 0) {
          history.push('/ai/blueprints/welcome')
          return
        }

        history.push('/ai/blueprints')
      }
    })

    const isEditCampaignPath = () => {
      const path = history.location.pathname
      const regex = /^\/campaigns\/edit\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12})\/details$/
      return regex.test(path)
    }

    const dspItem: EuiSideNavItemType<any> = {
      id: 'adcritter-dsp',
      name: 'AdCritter DSP',
      icon: <EuiIcon type={'dashboardApp'} />,
      onClick: () => history.push('/dashboard'),
      items: []
    }

    dspItem.items?.push({
      id: 'dashboard',
      name: 'Dashboard',
      forceOpen: true,
      renderItem: props => <SubNavItem {...props} isSelected={history.location.pathname.includes('/dashboard')} />,
      onClick: () => history.push('/dashboard')
    })

    dspItem.items?.push({
      id: 'master-campaigns',
      name: 'Master Campaigns',
      forceOpen: true,
      renderItem: props => <SubNavItem {...props} isSelected={history.location.pathname.includes('/masterCampaigns') || (isEditCampaignPath() && !!campaign?.masterCampaignId)} />,
      onClick: () => history.push('/masterCampaigns')
    })

    dspItem.items?.push({
      id: 'solo-campaigns',
      name: 'Solo Campaigns',
      forceOpen: true,
      renderItem: props => <SubNavItem {...props} isSelected={(history.location.pathname.includes('/campaigns') && !isEditCampaignPath()) || (isEditCampaignPath() && !campaign?.masterCampaignId)} />,
      onClick: () => history.push('/campaigns')
    })

    !whiteLabel?.hideReports() &&
      dspItem.items?.push({
        id: 'reports',
        name: 'Reports',
        renderItem: props => <SubNavItem {...props} isSelected={history.location.pathname.includes('/reports')} />,
        onClick: () => history.push('/reports')
      })

    !whiteLabel?.isVista() &&
      dspItem.items?.push({
        id: 'audience-pixel',
        name: 'Audience Pixel',
        renderItem: props => <SubNavItem {...props} isSelected={history.location.pathname.includes('/audience-pixel')} />,
        onClick: () => history.push('/audience-pixel')
      })

    isAgency &&
      canUseTemplateBuilder &&
      (!campaignApprovalEnabled || canCreateCampaigns) &&
      dspItem.items?.push({
        id: 'templates',
        name: 'Templates',
        renderItem: props => <SubNavItem {...props} isSelected={history.location.pathname.includes('/templateBuild')} />,
        onClick: () => {
          dispatch(initializeCampaignCreate())
          CreateCampaignStorageManager.DeleteFromLocalStorage()
          history.push('/templateBuild')
        }
      })

    dspItem.items?.push({
      id: 'client-details',
      name: 'Client Details',
      renderItem: props => <SubNavItem {...props} isSelected={history.location.pathname.includes('/client-details')} />,
      onClick: () => history.push('/client-details')
    })

    !isVendastaUser &&
      !isShopifyUser &&
      !whiteLabel?.hideKnowledgeBase() &&
      dspItem.items?.push({
        id: 'contact-support',
        name: 'Contact Support',
        renderItem: props => <SubNavItem {...props} />,
        onClick: () => {
          window.open(`https://adcritter.com/contact?${whiteLabel?.isAgencies() ? 'questionType=Agencies' : ''}`, '_blank')
        }
      })

    items.push(dspItem)

    setNavItems(items)
  }, [hasPermission, whiteLabel, isAgency, canUseTemplateBuilder, campaignApprovalEnabled, canCreateCampaigns, isVendastaUser, isShopifyUser, masterCampaignPlansQuery.data, aiPlanBlueprintsQuery.data, aiPlansQuery.data, dispatch, currentAccount, campaign, accountsQuery, accountOptions])

  return (
    currentUser && (
      <AcFlyout aria-label='Main navigation' size={224} onClose={() => {}} side={'left'} hideCloseButton={true} type={'push'} role={null} ownFocus={false}>
        <EuiSideNav
          items={navItems}
          style={{
            paddingTop: '12px',
            paddingLeft: '12px',
            width: '100%'
          }}
          mobileBreakpoints={[undefined]}
        />
      </AcFlyout>
    )
  )
}
